<template>
    <CompassTableRow
        :class="{ 'ocr-verification-products-table__row--not-expandable': !canExpand }"
        :expanded="isExpanded"
        @update:expanded="(expanded) => onToggleExpand(product, index, expanded)"
    >
        <CompassTableBodyCell maxWidth="200px" minWidth="200px">
            <CompassFormItem label="" :error="lineError.description">
                <CompassInput
                    class="ocr-verification-products-table__input"
                    :modelValue="product.description"
                    :disabled="props.disabled"
                    :error="!!lineError.description"
                    @update:modelValue="onDescriptionUpdate($event, product, index)"
                />
            </CompassFormItem>
        </CompassTableBodyCell>
        <CompassTableBodyCell v-if="props.isLineItemsEnabled" maxWidth="80px" minWidth="80px">
            <CompassInput
                class="ocr-verification-products-table__input"
                :modelValue="product.quantity"
                :disabled="props.disabled"
                placeholder="-"
                :type="CompassEnums.InputType.NUMBER"
                @update:modelValue="onQuantityUpdate($event, product, index)"
            />
        </CompassTableBodyCell>
        <CompassTableBodyCell maxWidth="140px" minWidth="140px">
            <CompassFormItem label="" :error="lineError.priceWithoutTax">
                <CompassInput
                    class="ocr-verification-products-table__input"
                    :modelValue="product.priceWithoutTax"
                    :disabled="props.disabled"
                    :error="!!lineError.priceWithoutTax"
                    inputmode="decimal"
                    @update:modelValue="onPriceInput($event, product, index)"
                    @blur="formatFieldToTwoDecimals(product, 'priceWithoutTax')"
                >
                    <template v-if="props.currencySymbol" #suffix>
                        {{ props.currencySymbol }}
                    </template>
                </CompassInput>
            </CompassFormItem>
        </CompassTableBodyCell>
        <CompassTableBodyCell maxWidth="110px" minWidth="110px">
            <CompassFormItem label="" :error="lineError.taxRateId">
                <CompassSelect
                    class="ocr-verification-products-table__input"
                    :modelValue="product.taxRateId ?? ''"
                    :disabled="props.disabled"
                    filterable
                    :placeholder="$t.__('ocr.OcrVerificationForm.select_rate')"
                    :error="!!lineError.taxRateId"
                    @update:modelValue="onTaxRateUpdate($event, product, index)"
                >
                    <CompassDropdownOption
                        v-for="item in props.taxRates"
                        :key="item.id"
                        :label="`${item.rate}%`"
                        :value="item.id"
                    />
                </CompassSelect>
            </CompassFormItem>
        </CompassTableBodyCell>
        <CompassTableBodyCell v-if="props.isLineItemsEnabled" maxWidth="140px" minWidth="140px">
            <CompassSelect
                class="ocr-verification-products-table__input"
                :modelValue="product.unit"
                :disabled="props.disabled"
                filterable
                :placeholder="$t.__('ocr.OcrVerificationProductsTable.select_unit')"
                @update:modelValue="onUnitUpdate($event, product, index)"
            >
                <CompassDropdownOption :value="null" :label="$t.__('common.labels.none')" />
                <CompassDropdownOption
                    v-for="unit in props.units"
                    :key="unit.id"
                    :label="unit.label"
                    :value="unit.id"
                />
            </CompassSelect>
        </CompassTableBodyCell>
        <template v-if="!props.isLineItemsEnabled">
            <CompassTableBodyCell maxWidth="140px" minWidth="140px">
                <CompassInput
                    class="ocr-verification-products-table__input"
                    :modelValue="taxAmount"
                    :disabled="props.disabled"
                    inputmode="decimal"
                    @update:modelValue="onTaxAmountInput"
                    @blur="formatOverrideOnBlur('taxAmountOverride')"
                >
                    <template v-if="props.currencySymbol" #suffix>
                        {{ props.currencySymbol }}
                    </template>
                </CompassInput>
            </CompassTableBodyCell>
            <CompassTableBodyCell maxWidth="140px" minWidth="140px">
                <CompassInput
                    class="ocr-verification-products-table__input"
                    :modelValue="amountIncludingTax"
                    :disabled="props.disabled"
                    inputmode="decimal"
                    @update:modelValue="onAmountIncludingTaxInput"
                    @blur="formatOverrideOnBlur('amountIncludingTaxOverride')"
                >
                    <template v-if="props.currencySymbol" #suffix>
                        {{ props.currencySymbol }}
                    </template>
                </CompassInput>
            </CompassTableBodyCell>
        </template>
        <CompassTableBodyCell v-if="props.isLineItemsEnabled" maxWidth="260px" minWidth="260px">
            <div class="ocr-verification-products-table__catalogue-cell">
                <CompassAutoComplete
                    :key="catalogueLinkSelectKey"
                    class="ocr-verification-products-table__input"
                    :modelValue="product.catalogueLink"
                    :defaultValues="catalogueLinkDefaultValues"
                    :disabled="props.disabled"
                    :results="catalogueResults"
                    :loading="isSearchingCatalogue"
                    :triggerAfter="0"
                    menuClass="ocr-form__select-fit-dropdown"
                    :placeholder="$t.__('common.actions.search')"
                    :noResultsLabel="$t.__('common.labels.no_result')"
                    :actions="isSearchingCatalogue ? [] : catalogueActions"
                    @update-search="handleCatalogueSearch"
                    @update:modelValue="onCatalogueLinkUpdate($event, product, index)"
                    @focus="handleCatalogueFocus"
                />
                <CompassTooltip
                    :content="$t.__('ocr.OcrVerificationProductsTable.update_product')"
                    :disabled="!product.catalogueLink"
                >
                    <CompassIconButton
                        icon="edit"
                        :disabled="props.disabled"
                        :size="CompassEnums.Size.MEDIUM"
                        :class="{
                            'ocr-verification-products-table__catalogue-cell__edit--hidden':
                                !product.catalogueLink,
                        }"
                        @click="onEdit(product)"
                    />
                </CompassTooltip>
            </div>
        </CompassTableBodyCell>
        <CompassTableBodyCell maxWidth="200px" minWidth="200px">
            <CompassFormItem label="" :error="lineError.accountingCode">
                <CompassSelect
                    class="ocr-verification-products-table__input"
                    :modelValue="product.accountingCode"
                    :disabled="props.disabled"
                    filterable
                    :placeholder="$t.__('ocr.OcrVerificationForm.select_code')"
                    :error="!!lineError.accountingCode"
                    @update:modelValue="onAccountingCodeUpdate($event, product, index)"
                >
                    <CompassDropdownOption :value="null" :label="$t.__('common.labels.none')" />
                    <CompassDropdownOption
                        v-for="item in props.accountingCodes"
                        :key="item.id"
                        :label="`${item.code} - ${item.label}`"
                        :value="item.id"
                    />
                </CompassSelect>
            </CompassFormItem>
        </CompassTableBodyCell>
        <CompassTableBodyCell v-if="showAnalyticCodeColumn" maxWidth="200px" minWidth="200px">
            <CompassInput
                class="ocr-verification-products-table__input"
                :modelValue="product.analyticCode"
                :disabled="props.disabled"
                :placeholder="$t.__('ocr.OcrVerificationProductsTable.analytic_code')"
                @update:modelValue="onAnalyticCodeUpdate($event, product, index)"
            />
        </CompassTableBodyCell>
        <CompassTableBodyCell idColumn="delete">
            <CompassTooltip :content="$t.__('common.actions.duplicate')" :arrow="false">
                <CompassIconButton
                    icon="duplicate"
                    :disabled="props.disabled"
                    :size="CompassEnums.Size.MEDIUM"
                    @click="onRequestDuplicate(index)"
                />
            </CompassTooltip>
            <CompassTooltip
                v-if="canDelete"
                :content="$t.__('common.actions.delete')"
                :arrow="false"
            >
                <CompassIconButton
                    icon="bin"
                    :disabled="props.disabled"
                    :variant="CompassEnums.IconButtonVariant.DESTRUCTIVE"
                    :size="CompassEnums.Size.MEDIUM"
                    @click="onRequestDelete(index)"
                />
            </CompassTooltip>
        </CompassTableBodyCell>
    </CompassTableRow>
    <CompassTableRow :visible="isExpanded && canExpand" subrow>
        <template #subrow>
            <CompassTable displayEmptyExpandableColumn>
                <CompassTableBody>
                    <CompassTableRow>
                        <CompassTableBodyCell>
                            <div class="ocr-verification-products-table__details">
                                <div class="ocr-verification-products-table__details__top">
                                    <CompassFormItem
                                        :label="$t.__('ocr.OcrVerificationForm.stock_entry_date')"
                                    >
                                        <CompassDatePicker
                                            :modelValue="stockOutDateValue"
                                            :disabled="props.disabled"
                                            :formatDateDisplayed="props.formatDateDisplayed"
                                            @update:modelValue="
                                                onStockOutDateUpdate($event, product, index)
                                            "
                                        />
                                    </CompassFormItem>
                                    <CompassFormItem
                                        :label="$t.__('ocr.OcrVerificationForm.warehouse')"
                                    >
                                        <CompassSelect
                                            :modelValue="product.warehouseId"
                                            :disabled="props.disabled"
                                            filterable
                                            placeholder="-"
                                            @update:modelValue="
                                                onWarehouseUpdate($event, product, index)
                                            "
                                        >
                                            <CompassDropdownOption label="-" value="" />
                                            <CompassDropdownOption
                                                v-for="warehouse in props.warehouses"
                                                :key="warehouse.id"
                                                :label="warehouse.label"
                                                :value="warehouse.id"
                                            />
                                        </CompassSelect>
                                    </CompassFormItem>
                                </div>
                                <div v-if="isSerializedStock">
                                    <CompassFormItem
                                        :label="$t.__('ocr.OcrVerificationForm.serial_numbers')"
                                    >
                                        <CompassInput
                                            :type="CompassEnums.InputType.TEXTAREA"
                                            :disabled="props.disabled"
                                            :modelValue="product.serialNumbers"
                                            :placeholder="
                                                $t.__('ocr.OcrVerificationForm.serial_numbers')
                                            "
                                            @update:modelValue="
                                                onSerialNumbersUpdate($event, product, index)
                                            "
                                        />
                                        <span class="typographies-paragraph-small-regular">
                                            {{
                                                $t.__(
                                                    'ocr.OcrVerificationForm.stock.serialized.info',
                                                )
                                            }}
                                        </span>
                                    </CompassFormItem>
                                </div>
                            </div>
                        </CompassTableBodyCell>
                    </CompassTableRow>
                </CompassTableBody>
            </CompassTable>
        </template>
    </CompassTableRow>
</template>

<script setup>
import { computed, inject, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { search } from 'api/searchEngineService';
import {
    formatFieldToTwoDecimals,
    limitToTwoDecimals,
    calculateTotalWithTax,
    PRECISION_FACTOR,
    AMOUNT_DECIMAL_PLACES,
} from 'composables/useOcrProducts';
import Item from 'api/item';
import { CompassEnums } from '@sellsy/compass2';

const $t = inject('$t');
const $pushToast = inject('$pushToast');

const props = defineProps({
    product: {
        type: Object,
        required: true,
    },
    index: {
        type: Number,
        required: true,
    },
    canExpand: {
        type: Boolean,
        default: false,
    },
    isExpanded: {
        type: Boolean,
        default: false,
    },
    canDelete: {
        type: Boolean,
        default: false,
    },
    lineError: {
        type: Object,
        default: () => ({
            description: '',
            priceWithoutTax: '',
            taxRateId: '',
            accountingCode: '',
        }),
    },
    taxRates: {
        type: Array,
        default: () => [],
    },
    accountingCodes: {
        type: Array,
        default: () => [],
    },
    units: {
        type: Array,
        default: () => [],
    },
    warehouses: {
        type: Array,
        default: () => [],
    },
    showAnalyticCode: {
        type: Boolean,
        default: false,
    },
    isLineItemsEnabled: {
        type: Boolean,
        default: false,
    },
    currencySymbol: {
        type: String,
        default: '',
    },
    formatDateDisplayed: {
        type: String,
        default: null,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits([
    'description-update',
    'quantity-update',
    'price-without-tax-update',
    'tax-rate-update',
    'tax-amount-override',
    'amount-including-tax-override',
    'unit-update',
    'catalogue-link-update',
    'accounting-code-update',
    'analytic-code-update',
    'toggle-expand',
    'stock-out-date-update',
    'warehouse-update',
    'serial-numbers-update',
    'request-delete',
    'request-duplicate',
    'product-updated',
]);

const onDescriptionUpdate = (value, product, index) => {
    emit('description-update', value, product, index);
};
const onQuantityUpdate = (value, product, index) => {
    emit('quantity-update', value, product, index);
};
const onPriceInput = (value, product, index) => {
    const limited = limitToTwoDecimals(value);
    emit('price-without-tax-update', limited, product, index);
};

const onTaxRateUpdate = (value, product, index) => {
    emit('tax-rate-update', value, product, index);
};
const onUnitUpdate = (value, product, index) => {
    emit('unit-update', value, product, index);
};
const onCatalogueLinkUpdate = (value, product, index) => {
    emit('catalogue-link-update', value, product, index);
};
const onAccountingCodeUpdate = (value, product, index) => {
    emit('accounting-code-update', value, product, index);
};
const onAnalyticCodeUpdate = (value, product, index) => {
    emit('analytic-code-update', value, product, index);
};
const onToggleExpand = (product, index, expanded) => {
    emit('toggle-expand', product, index, expanded);
};
const onStockOutDateUpdate = (value, product, index) => {
    emit('stock-out-date-update', value, product, index);
};
const onWarehouseUpdate = (value, product, index) => {
    emit('warehouse-update', value, product, index);
};
const onSerialNumbersUpdate = (value, product, index) => {
    emit('serial-numbers-update', value, product, index);
};
const onRequestDelete = (index) => {
    emit('request-delete', index);
};
const onRequestDuplicate = (index) => {
    emit('request-duplicate', index);
};
const onProductUpdated = (itemId, updatedName, accountingPurchaseCodeId) => {
    emit('product-updated', itemId, updatedName, accountingPurchaseCodeId);
};
const stockOutDateValue = computed(() => props.product?.stockOutDate ?? '');
const showAnalyticCodeColumn = computed(() => props.showAnalyticCode && props.isLineItemsEnabled);

const totalWithoutTaxInCents = computed(() => {
    const price = parseFloat(String(props.product.priceWithoutTax ?? '').replace(',', '.'));
    const quantity = props.isLineItemsEnabled ? 1 : parseFloat(props.product.quantity) || 1;
    if (Number.isNaN(price)) {
        return null;
    }
    return Math.round(price * quantity * PRECISION_FACTOR);
});

const productTaxInCents = computed(() => {
    const withoutTaxInCents = totalWithoutTaxInCents.value;
    const taxItem = props.taxRates.find((item) => item.id === props.product.taxRateId);
    if (withoutTaxInCents === null || !taxItem) {
        return null;
    }
    return Math.round((withoutTaxInCents * Number(taxItem.rate)) / 100);
});

const computedTaxAmount = computed(() => {
    if (productTaxInCents.value === null) {
        return null;
    }
    return (productTaxInCents.value / PRECISION_FACTOR)
        .toFixed(AMOUNT_DECIMAL_PLACES)
        .replace('.', ',');
});

const computedAmountIncludingTax = computed(() => {
    if (productTaxInCents.value === null) {
        return null;
    }
    return ((totalWithoutTaxInCents.value + productTaxInCents.value) / PRECISION_FACTOR)
        .toFixed(AMOUNT_DECIMAL_PLACES)
        .replace('.', ',');
});

const taxAmount = computed(() => props.product.taxAmountOverride ?? computedTaxAmount.value);
const amountIncludingTax = computed(
    () => props.product.amountIncludingTaxOverride ?? computedAmountIncludingTax.value,
);

watch(
    [
        () => props.product.priceWithoutTax,
        () => props.product.quantity,
        () => props.product.taxRateId,
    ],
    () => {
        emit('tax-amount-override', null, props.product, props.index);
        emit('amount-including-tax-override', null, props.product, props.index);
    },
);

const onTaxAmountInput = (value) => {
    const limited = limitToTwoDecimals(value);
    emit('tax-amount-override', limited, props.product, props.index);
    const withoutTaxInCents = totalWithoutTaxInCents.value;
    const tax = parseFloat(String(limited ?? '').replace(',', '.'));
    if (withoutTaxInCents !== null && !Number.isNaN(tax)) {
        const totalWithTax = calculateTotalWithTax(withoutTaxInCents / PRECISION_FACTOR, tax);
        emit(
            'amount-including-tax-override',
            totalWithTax.toFixed(AMOUNT_DECIMAL_PLACES).replace('.', ','),
            props.product,
            props.index,
        );
    }
};

const onAmountIncludingTaxInput = (value) => {
    const limited = limitToTwoDecimals(value);
    emit('amount-including-tax-override', limited, props.product, props.index);
    const withoutTaxInCents = totalWithoutTaxInCents.value;
    const amountIncludingTax = parseFloat(String(limited ?? '').replace(',', '.'));
    if (withoutTaxInCents !== null && !Number.isNaN(amountIncludingTax)) {
        const amountIncludingTaxInCents = Math.round(amountIncludingTax * PRECISION_FACTOR);
        emit(
            'tax-amount-override',
            ((amountIncludingTaxInCents - withoutTaxInCents) / PRECISION_FACTOR)
                .toFixed(AMOUNT_DECIMAL_PLACES)
                .replace('.', ','),
            props.product,
            props.index,
        );
    }
};

const formatOverrideOnBlur = (field) => {
    const value = props.product[field];
    if (value !== null && value !== '') {
        const parsed = parseFloat(String(value).replace(',', '.'));
        if (!Number.isNaN(parsed)) {
            const eventName =
                field === 'taxAmountOverride'
                    ? 'tax-amount-override'
                    : 'amount-including-tax-override';
            emit(
                eventName,
                parsed.toFixed(AMOUNT_DECIMAL_PLACES).replace('.', ','),
                props.product,
                props.index,
            );
        }
    }
};

const catalogueLinkDefaultValues = computed(() => {
    const link = props.product.catalogueLink;
    return link?.id ? [{ value: link.id, label: link.label }] : [];
});

const catalogueLinkSelectKey = ref(0);

const catalogueResults = ref([]);
const isSearchingCatalogue = ref(false);
let catalogueSearchAbortController = null;

const abortCatalogueSearch = () => {
    if (catalogueSearchAbortController) {
        catalogueSearchAbortController.abort();
        catalogueSearchAbortController = null;
    }
};

/** Triggers a ref change so CompassAutoComplete re-renders the cached dropdown results on focus */
const handleCatalogueFocus = () => {
    catalogueResults.value = [...catalogueResults.value];
};

const handleCatalogueSearch = async (query) => {
    if (!query) {
        abortCatalogueSearch();
        catalogueResults.value = [];
        isSearchingCatalogue.value = false;
        return;
    }

    abortCatalogueSearch();

    const controller = new AbortController();
    catalogueSearchAbortController = controller;

    isSearchingCatalogue.value = true;
    await nextTick();

    if (catalogueSearchAbortController !== controller) {
        return;
    }

    catalogueResults.value = [];

    try {
        const {
            data: { data },
        } = await search({
            query,
            type: ['item.product', 'item.service'],
            signal: controller.signal,
        });

        if (catalogueSearchAbortController !== controller) {
            return;
        }

        catalogueResults.value = (data ?? []).map((result) => ({
            label: result.name || result.number,
            id: result.object.id,
            value: result.object.id,
            itemType: result.object.type === 'item.service' ? 'service' : 'item',
            icon: 'obj-slsy-product',
        }));
    } catch {
        if (catalogueSearchAbortController !== controller) {
            return;
        }
        catalogueResults.value = [];
        $pushToast({
            type: CompassEnums.ToastType.ERROR,
            icon: true,
            text: $t.__('ocr.OcrVerificationForm.error_generic'),
            timeout: 5000,
        });
    } finally {
        if (catalogueSearchAbortController === controller) {
            catalogueSearchAbortController = null;
            isSearchingCatalogue.value = false;
        }
    }
};

const createCatalogueItem = (type) => {
    cleanupItemUpdateHandler();

    const handleItemCreated = async (data) => {
        const itemId = data?.item?.id;
        if (!itemId) {
            cleanupItemUpdateHandler();
            return;
        }

        try {
            const { name, itemType } = await getUpdatedProductData(itemId);
            const catalogueLink = { id: itemId, label: name, value: itemId, itemType };
            onCatalogueLinkUpdate(catalogueLink, props.product, props.index);
            catalogueLinkSelectKey.value++;
        } catch {
            $pushToast({
                type: CompassEnums.ToastType.ERROR,
                icon: true,
                text: $t.__('ocr.OcrVerificationForm.error_generic'),
                timeout: 5000,
            });
        } finally {
            cleanupItemUpdateHandler();
        }
    };

    currentItemUpdateHandler = handleItemCreated;
    EventBus.on('catalogueItem:updated', handleItemCreated);

    window.catalogueItem.edit(type, undefined, false, '', {
        preventRelocate: true,
        hideAddAnother: true,
    });
};

const catalogueActions = [
    {
        label: $t.__('ocr.OcrVerificationProductsTable.add_product'),
        callback: () => createCatalogueItem('item'),
    },
    {
        label: $t.__('ocr.OcrVerificationProductsTable.add_service'),
        callback: () => createCatalogueItem('service'),
    },
];

onBeforeUnmount(() => {
    abortCatalogueSearch();
    cleanupItemUpdateHandler();
});

const isSerializedStock = computed(() => props.product?.related?.serialized_stock === 'Y');

const getUpdatedProductData = async (itemId) => {
    const { data } = await Item.getItemById(itemId);
    const itemType = data.type === 'service' ? 'service' : 'item';
    return {
        name: data.name,
        accountingPurchaseCodeId: data.accounting_purchase_code_id,
        itemType,
    };
};

let currentItemUpdateHandler = null;

const cleanupItemUpdateHandler = () => {
    if (currentItemUpdateHandler) {
        EventBus.off('catalogueItem:updated', currentItemUpdateHandler);
        currentItemUpdateHandler = null;
    }
};

const onEdit = (product) => {
    const itemId = product.catalogueLink?.id;
    if (!itemId) {
        return;
    }

    cleanupItemUpdateHandler();

    const handleItemUpdate = async () => {
        try {
            const { name, accountingPurchaseCodeId } = await getUpdatedProductData(itemId);
            if (name) {
                onProductUpdated(itemId, name, accountingPurchaseCodeId);
            }
        } catch {
            $pushToast({
                type: CompassEnums.ToastType.ERROR,
                icon: true,
                text: $t.__('ocr.OcrVerificationForm.error_generic'),
                timeout: 5000,
            });
        } finally {
            cleanupItemUpdateHandler();
        }
    };

    currentItemUpdateHandler = handleItemUpdate;
    EventBus.on('catalogueItem:updated', handleItemUpdate);

    const type = product.catalogueLink.itemType ?? 'item';
    window.catalogueItem.edit(type, itemId, false, '', {
        preventRelocate: true,
        hideAddAnother: true,
    });
};
</script>

<style scoped lang="scss">
:deep(.compass-table__cell-container) {
    width: 100%;
}

.ocr-verification-products-table {
    &__catalogue-cell {
        display: flex;
        align-items: center;
        gap: var(--spacing-1);
        width: 100%;

        &__edit--hidden {
            visibility: hidden;
            pointer-events: none;
        }

        .ocr-verification-products-table__input {
            flex: 1;
            min-width: 0;
        }
    }
    &__input {
        width: 100%;
    }

    &__details {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-3);
        &__top {
            display: flex;
            gap: var(--spacing-3);
        }
        &__row--not-expandable :deep(.compass-table__row-expand-button) {
            display: none;
        }
    }
}

:deep(.compass-table__cell) {
    vertical-align: top;
}

:deep(.compass-form-item) {
    width: 100%;

    .compass-form-item__caption-container,
    .compass-form-item__caption {
        width: 100%;
    }

    .compass-form-item__caption {
        white-space: normal;
    }
}
</style>
